/* ============================================================
 * 归途 · Homeward —— 静态数据表（装备 / 技能 / 境界 / 兑换）
 * 这里是纯数据定义，不依赖 DOM 与游戏状态，方便前后端共享或复用到工具脚本。
 * ============================================================ */

export const SLOT_DEFS = {
  helmet: { label: '头盔', icon: '盔', base: ['粗布帽', '皮帽', '铁盔', '战盔'] },
  weapon: { label: '武器', icon: '武', base: [] },
  clothes: { label: '衣服', icon: '衣', base: ['布衣', '皮甲', '锁甲', '战袍'] },
  shoes: { label: '鞋子', icon: '靴', base: ['布靴', '皮靴', '铁靴', '疾行靴'] },
  gloves: { label: '手套', icon: '手', base: ['布手套', '皮手套', '铁手套', '锐爪手套'] },
  jewelry: { label: '首饰', icon: '饰', base: ['木珠串', '铜环', '玉坠', '灵玉项链'] },
  treasure: { label: '灵宝', icon: '灵', base: ['灵宝'] }   // 金色洞府终点掉落，自带特殊属性
};

export const WEAPON_KINDS = [
  { name: '长弓', icon: '弓' },
  { name: '短剑', icon: '剑' },
  { name: '爪', icon: '爪' },
  { name: '法杖', icon: '杖' },
  { name: '盾牌', icon: '盾' }
];

/* 武器基底词缀（implicit）：
 *   只要装备的是这种武器就一定自带，不计入随机词缀条数，
 *   洗练也不会洗掉（参考流放之路的"基底词缀"）。
 */
export const WEAPON_BASE_MODS = {
  // 盾牌自带：格挡几率 +15%
  '盾牌': [{ key: 'blockPct', value: 15 }],
  // 长弓自带：攻击时 50% 几率额外射出一支箭矢
  '长弓': [{ key: 'extraArrowPct', value: 50 }]
};

/* 首饰基底词缀池：
 *   每件首饰生成时从下面随机抽一条作为基底（生成后就固定，洗练不会变）。
 */
export const JEWELRY_BASE_POOL = [
  { mods: [{ key: 'lifeRegen', value: 5 }], label: '生命恢复 +5' },
  { mods: [{ key: 'mpRegen', value: 5 }], label: '灵力恢复 +5' },
  { mods: [{ key: 'allAttr', value: 5 }], label: '全能力 +5' },
  { mods: [{ key: 'agility', value: 8 }], label: '敏捷 +8' },
  { mods: [{ key: 'strength', value: 8 }], label: '力量 +8' },
  { mods: [{ key: 'intelligence', value: 8 }], label: '智慧 +8' },
  { mods: [{ key: 'armorPct', value: 15 }], label: '防御力提高 15%' },
  { mods: [{ key: 'evasionPct', value: 15 }], label: '闪避提高 15%' },
  { mods: [{ key: 'speedPct', value: 15 }], label: '移动速度提高 15%' },
  { mods: [{ key: 'critChance', value: 3 }], label: '暴击率 +3%' }
];

export const RARITY_DEFS = {
  common: { name: '普通', color: '#d8d3c2', tier: 1 },
  magic: { name: '魔法', color: '#67b6ff', tier: 2 },
  rare: { name: '稀有', color: '#ffd265', tier: 3 },
  legend: { name: '传奇', color: '#ff9df0', tier: 4 },  // 灵宝专用品级
  unique: { name: '独特', color: '#e08b2e', tier: 5 }   // 独特装备（暗金）：固定词缀 + 专属机制
};

// 锁定商店商品所需金币：白色 10 / 蓝色 20 / 黄色 30
export const SHOP_LOCK_COST = { common: 10, magic: 20, rare: 30, legend: 50, unique: 100 };

/* ============================================================
 * 独特装备（暗金）
 *   · 固定名字 + 固定词缀：生成时把「区间」roll 成定值固化在装备上，洗练不会动
 *   · special：专属机制，recaclPlayerStats / 战斗里按 key 读取
 *   · lines：装备卡片上的说明行，{key} 会被 roll 出来的数值替换
 *   · 掉落途径：世界BOSS 宝箱 / 金色洞府终点（见 main.js 的 rollUniqueDrop）
 * ============================================================ */
export const UNIQUE_DEFS = [
  {
    id: 'wuzheInstrument',
    name: '武者的刑具',
    slot: 'weapon',
    weaponType: '短剑',
    icon: '剑',
    iconSet: 'weapon',
    price: 9000,
    stats: { atk: [59, 73], atkSpeed: [5, 10], strength: [15, 25] },
    mech: { firePer25: [5, 10], lavaRain: { perStr: 25, pct: 5 } },
    lines: [
      '物理伤害 59-73（成品 88-108）',
      '攻击速度提高 {atkSpeed}%',
      '+{strength} 力量',
      '每有 25 点力量，使用该武器击中时有 5% 的几率触发「熔岩骤雨」',
      '熔岩骤雨：在敌人上空召唤持续 1 秒的火雨，每颗火球造成力量 35% 的火属性伤害',
      '每有 25 点力量，附加 {firePer25} 点攻击火焰伤害'
    ]
  },
  {
    id: 'ironHoopBow',
    name: '铁箍战弓',
    slot: 'weapon',
    weaponType: '长弓',
    icon: '弓',
    iconSet: 'weapon',
    price: 9000,
    stats: { atk: [120, 200], atkSpeed: [5, 10] },
    mech: { pierceReturn: true },
    lines: [
      '物理伤害 120-200',
      '攻击速度提高 {atkSpeed}%',
      '箭矢穿透所有目标，并且归返'
    ]
  },
  {
    id: 'championPlate',
    name: '冠军胸甲',
    slot: 'clothes',
    icon: '衣',
    iconSet: 'clothes',
    price: 9000,
    stats: { armor: [1676, 2122], resCapPct: [1, 5] },
    mech: { critImmune: true },
    lines: [
      '护甲 {armor}',
      '不会受到暴击所造成的额外暴击伤害',
      '最大元素抗性上限 +{resCapPct}%'
    ]
  },
  {
    id: 'drifterArmour',
    name: '漂泊者护甲',
    slot: 'clothes',
    icon: '衣',
    iconSet: 'clothes',
    price: 9000,
    stats: { armor: [366, 507], strength: [20, 30] },
    mech: { atkPctPer2Str: 1, projPctPer2Str: 1 },
    lines: [
      '护甲 {armor}',
      '+{strength} 力量',
      '每 2 点力量提高 1% 的攻击伤害',
      '每 2 点力量提高 1% 的投射物伤害'
    ]
  },
  {
    id: 'thornBarkBoots',
    name: '荆皮护靴',
    slot: 'shoes',
    icon: '靴',
    iconSet: 'shoes',
    price: 9000,
    stats: { speedPct: [10, 20], thorns: [120, 250], hp: [140, 160] },
    mech: { thornsCritPct: 50 },
    lines: [
      '移动速度提高 {speedPct}%',
      '荆棘暴击几率 +50%',
      '{thorns} 荆棘伤害',
      '+{hp} 生命上限'
    ]
  },
  {
    id: 'chainBoots',
    name: '锁链战靴',
    slot: 'shoes',
    icon: '靴',
    iconSet: 'shoes',
    price: 9000,
    stats: { speedPct: 10, armor: [280, 330] },
    mech: { stunImmune: true },
    lines: [
      '移动速度提高 10%',
      '护甲 {armor}',
      '免疫晕眩'
    ]
  },
  {
    id: 'blackSteelTowerShield',
    name: '黑钢塔盾',
    slot: 'weapon',
    weaponType: '盾牌',
    icon: '盾',
    iconSet: 'weapon',
    price: 9000,
    stats: { armor: 450 },
    mech: { blockPer5Str: 1 },
    lines: [
      '格挡几率 15%（基础格挡）',
      '护甲 450',
      '每 5 点力量 +1% 的格挡几率'
    ]
  },
  {
    id: 'wallShield',
    name: '瓦尔塔盾',
    slot: 'weapon',
    weaponType: '盾牌',
    icon: '盾',
    iconSet: 'weapon',
    price: 9000,
    stats: { armor: 650, blockPct: 20 },
    mech: { blockHealPct: 5 },
    lines: [
      '格挡几率 35%（基础 15% + 额外 20%）',
      '格挡时回复生命上限的 5%',
      '护甲 650'
    ]
  }
];

/* ============================================================
 * 灵宝特殊属性（金色洞府终点掉落的传奇装备专用）
 *   这类属性不属于常规词缀：要么改机制，要么给特殊收益。
 *   kind: 'passive' 常驻特效 / 'skill' 附带主动技能
 * ============================================================ */
export const TREASURE_SPECIALS = [
  {
    key: 'strToHpDouble', kind: 'passive', name: '蛮力之心',
    desc: '每点力量增加的生命上限从 5 点提升到 10 点'
  },
  {
    key: 'alwaysFirst', kind: 'passive', name: '先手符诏',
    desc: '永远先手出手，不受移动速度影响'
  },
  {
    key: 'goldPerWin', kind: 'passive', name: '聚宝灵纹',
    desc: '每场战斗胜利时，必然额外获得 10 金币'
  },
  {
    key: 'hpFlat1000', kind: 'passive', name: '不坏金身',
    desc: '生命上限最大值提高 1000'
  },
  {
    key: 'grantSkill', kind: 'skill', name: '灵宝传承',
    desc: '附带一个强力主动技能（装备后自动学会）'
  },
  {
    key: 'lifesteal10', kind: 'passive', name: '血饮魔纹',
    desc: '每次攻击命中额外回复 5% 最大生命'
  },
  {
    key: 'magicAmp', kind: 'passive', name: '蓝宝灵韵',
    desc: '已装备的蓝色（魔法）装备属性增幅 50%~150%'
  },
  {
    // 稀有灵宝：出现几率远低于其它灵宝（见 pickTreasureSpecial 的权重）
    key: 'levelBoost', kind: 'passive', name: '破境符诏', rare: true,
    desc: '临时强行提升 5 个小境界（例如从「武者三境·中期」提到「武者五境·初期」）：挑战世界BOSS时按 +5 级计算境界门槛（其它地方不生效）'
  }
];

// 被动技能石可选的属性增幅（参考流放之路的辅助宝石思路）
export const GEM_OPTIONS = [
  { id: 'weaponDamage', name: '武器伤害提高', min: 15, max: 25, unit: '%' },
  { id: 'meleeDamage', name: '近战伤害提高', min: 18, max: 30, unit: '%' },
  { id: 'spellDamage', name: '法术伤害提高', min: 15, max: 25, unit: '%' },
  { id: 'summonDamage', name: '召唤伤害提高', min: 20, max: 35, unit: '%' },
  { id: 'life', name: '生命上限提高', min: 15, max: 30, unit: '' },
  { id: 'energy', name: '灵力上限提高', min: 8, max: 16, unit: '' },
  { id: 'moveSpeed', name: '移动速度提高', min: 3, max: 6, unit: '%' },
  { id: 'armorUp', name: '护甲提高', min: 10, max: 20, unit: '' },
  // 专属辅助：只能镶嵌到指定主动技能上（不给数值，而是改技能行为）
  {
    id: 'earthshaker', name: '震撼大地（辅助）', min: 0, max: 0, unit: '',
    support: true, onlySkill: 'yangClaw',
    short: '阳爪专属：脚步震裂大地，冲击波随机触发 1~5 次（每次伤害独立结算与显示）',
    desc: '仅可镶嵌到「阳爪」：施放阳爪时你的脚步震裂大地，冲击波随机触发 1~5 次，' +
      '每次冲击波的伤害单独计算、单独显示（对范围内所有敌人，每次都是「普通攻击伤害的 50% + 力量」）。'
  },
  /* ---------- 投射物辅助（只认「投射物」标签，不绑定任何具体技能）----------
   *   带 tags: ['projectile'] 的技能都能镶：破空斩（剑气）、强力射击（箭矢）、
   *   盾墙（格挡触发的碎石崩裂）。以后新做带投射物的技能，只要在技能定义里写上
   *   tags: ['projectile']，这两个辅助就自动能用，不需要再改代码。 */
  {
    id: 'multipleProjectiles', name: '多重投射物（辅助）', min: 0, max: 0, unit: '',
    support: true, projectileOnly: true,
    short: '伤害总降 25%、技能速度总降 20%，但多发射 2 个投射物（每个投射物独立结算伤害）',
    desc: '辅助带「投射物」标签的技能（破空斩·剑气 / 强力射击·箭矢 / 盾墙·碎石崩裂），可以发射额外的投射物。' +
      '同时降低被辅助技能的攻击和施法速度，并降低它们的伤害。' +
      '被辅助技能造成的伤害总降 25%；被辅助技能可以发射 2 个额外投射物；被辅助技能的技能速度总降 20%。' +
      '每个投射物各自独立结算一次伤害（所以只要打得中，总伤是提高的）。'
  },
  {
    id: 'projectileSpeed', name: '投射物加速（辅助）', min: 0, max: 0, unit: '',
    support: true, projectileOnly: true,
    short: '被辅助技能的投射物速度总增 40%（剑气/箭矢/碎石都飞得更快）',
    desc: '辅助带「投射物」标签的技能，使投射物速度加快：' +
      '被辅助技能的投射物速度总增 40%（破空斩的剑气、强力射击的箭矢、盾墙的碎石都生效）。'
  },
  // ---------- 通用击中辅助 ----------
  {
    id: 'execute', name: '处决（辅助）', min: 0, max: 0, unit: '',
    support: true,
    short: '你或敌人处于低血（≤50% 生命）时，被辅助技能的击中伤害总增 30%',
    desc: '辅助任意击中敌人的技能，使其对低血敌人造成更高伤害，当你低血时造成的伤害也更高。' +
      '在你低血（≤ 最大生命 50%）时，被辅助技能的击中伤害总增 30%；' +
      '被辅助技能对低血敌人（≤ 最大生命 50%）造成的击中伤害总增 30%。'
  },
  {
    id: 'flameLegion', name: '炎军（辅助）', min: 0, max: 0, unit: '',
    support: true,
    short: '造成伤害时 50% 几率叠加 1 层燃烧；每层每回合造成你最大生命 1% 的真实伤害',
    desc: '辅助任意击中敌人的技能：每次对敌人造成伤害时，有 50% 几率给敌人叠加 1 层燃烧效果。' +
      '每层燃烧在敌人每个回合承受你最大生命值 1% 的真实伤害（无视护甲与抗性），层数可以叠加（上限 20 层）。'
  },
  // ---------- 召唤生物辅助（只能镶嵌到召唤类技能）----------
  {
    id: 'meatShield', name: '肉盾（辅助）', min: 0, max: 0, unit: '',
    support: true, minionOnly: true,
    short: '被辅助技能的召唤生物：造成的伤害总降 35%、受到的伤害总降 35%',
    desc: '辅助产生召唤生物的技能。被辅助技能的召唤生物造成的伤害总降 35%；' +
      '被辅助技能的召唤生物受到的伤害总降 35%。'
  },
  {
    id: 'feedingFrenzy', name: '狂噬（辅助）', min: 0, max: 0, unit: '',
    support: true, minionOnly: true,
    short: '被辅助技能的召唤生物：造成的伤害总增 30%、受到的伤害总增 20%',
    desc: '辅助产生召唤生物的技能。被辅助技能的召唤生物造成的伤害总增 30%；' +
      '被辅助技能的召唤生物受到的伤害总增 20%。'
  },
  {
    id: 'minionMastery', name: '召唤生物专精（辅助）', min: 0, max: 0, unit: '',
    support: true, minionOnly: true,
    short: '被辅助的召唤生物技能等级 +1（召唤物生命与伤害都更高）',
    desc: '辅助产生召唤生物的技能：被辅助的召唤生物技能等级提高 +1，' +
      '召唤出来的生物生命与伤害都更高。'
  },
  {
    id: 'servant', name: '仆从（辅助）', min: 0, max: 0, unit: '',
    support: true, minionOnly: true,
    short: '召唤生物变为庞然大物：生命更高、伤害更高，但只能拥有一个召唤物',
    desc: '被辅助技能创造的召唤生物变为庞然大物，具有更多生命、造成更高的伤害，' +
      '导致只能拥有一个召唤物。'
  },
  {
    id: 'loyalty', name: '忠诚（辅助）', min: 0, max: 0, unit: '',
    support: true, minionOnly: true,
    short: '对你造成的伤害，由被辅助的伙伴的生命承受',
    desc: '辅助产生召唤生物的技能：对你造成的伤害由被辅助的伙伴的生命承受所有伤害。' +
      '伙伴的生命被打空后会倒下，溢出的一部分伤害才会落到你身上。'
  }
];

// 精英挑战奖励的随机主动技能
export const SKILL_POOL = [
  { id: 'heal', name: '恢复术', type: 'heal', cost: 6, desc: '恢复 35% 最大生命。' },
  { id: 'fireball', name: '火球术', type: 'spell', cost: 8, dmg: [42, 60], desc: '投出火球，造成 42~60 点火焰伤害。' },
  { id: 'lightning', name: '雷电术', type: 'spell', cost: 7, dmg: [32, 48], desc: '召来雷电，造成 32~48 点闪电伤害。' },
  { id: 'summonEnemy', name: '召唤普通敌人', type: 'summon', cost: 6, desc: '召唤普通敌人为你作战 3 次攻击。' }
];

// 主动技能石可开出的主动技能池（minStage 控制境界限制）
export const ACTIVE_SKILL_POOL = [
  {
    id: 'airSlash', name: '破空斩', type: 'melee', cost: 8, dmg: [44, 62], minStage: 1,
    tags: ['projectile'], projectileNote: '剑气',
    desc: '斩出破空剑气，造成 44~62 点伤害（群攻·贯穿一线）。' +
      '【标签：投射物】剑气属于投射物，「多重投射物 / 投射物加速」这类投射物辅助都能镶上去增幅它。'
  },
  {
    id: 'yangClaw', name: '阳爪', type: 'melee', cost: 9, dmg: [6, 10], atkPct: 15, minStage: 1,
    desc: '裂伤挥斩一名敌人：连续 6 次攻击，每次造成「攻击力的 15%」伤害（逐次独立结算与显示），' +
      '命中后使战场上的敌人陷入流血（每回合持续掉血）；与目标距离越远伤害越高，最多总增 100%。'
  },
  {
    id: 'powerShot', name: '强力射击', type: 'ranged', cost: 12, dmg: [50, 70], minStage: 1,
    charge: true, perfectTiming: true,
    tags: ['projectile'], projectileNote: '箭矢',
    desc: '蓄力一回合：本回合不造成伤害，期间你承受的敌人伤害提高 15%；' +
      '下一回合射出强力一击，造成 150% 伤害。' +
      '完美时机：施放时战斗场景中央出现进度条、标记左右循环滚动，按下「空格键」' +
      '让标记停在完美时机区间内即可触发 —— 箭矢命中时爆炸，对所有敌人额外造成 225% 伤害，' +
      '并晕眩敌人一回合。【标签：投射物】箭矢属于投射物，吃「多重投射物 / 投射物加速」。'
  },
  {
    id: 'bloodRebound',
    name: '气血反弹',
    type: 'toggle',
    cost: 5,
    minStage: 1,
    desc: '切换持续状态：每次被敌人攻击时，对敌人造成“自身最大生命 10% + 力量”的伤害（可重复激活/关闭）。'
  },
  { id: 'summonScorpion', name: '召唤蝎子', type: 'summon', cost: 5, minStage: 1, desc: '召唤沙漠毒蝎跟随你作战。' },
  { id: 'summonHyena', name: '召唤鬣狗', type: 'summon', cost: 5, minStage: 1, desc: '召唤鬣狗跟随你作战。' },
  { id: 'summonSnake', name: '召唤毒蛇', type: 'summon', cost: 5, minStage: 1, desc: '召唤沙漠毒蛇跟随你作战。' },
  { id: 'summonVulture', name: '召唤猎鹰', type: 'summon', cost: 5, minStage: 1, desc: '召唤猎鹰跟随你作战。' },
  { id: 'lightningArrow', name: '闪电箭', type: 'spell', cost: 7, dmg: [38, 55], minStage: 31, desc: '射出闪电箭，造成 38~55 点闪电伤害。' },
  { id: 'fireball', name: '火球术', type: 'spell', cost: 8, dmg: [42, 60], minStage: 31, desc: '投出火球，造成 42~60 点火焰伤害。' },
  { id: 'lightning', name: '雷电术', type: 'spell', cost: 7, dmg: [32, 48], minStage: 31, desc: '召来雷电，造成 32~48 点闪电伤害。' },
  { id: 'summonBats', name: '召唤蝙蝠', type: 'summon', cost: 7, minStage: 31, desc: '召唤蝙蝠群为你作战 3 次攻击。' },
  /* 格挡类持续技能：占用 25% 生命上限，常驻 +5% 格挡；
   * 每次成功格挡都会触发「碎石崩裂」——从身前射出碎石，
   * 对前方敌人造成「格挡几率% × 攻击力」的攻击伤害 + 25% 生命的真实伤害。 */
  {
    id: 'shieldWall', name: '盾墙', type: 'toggle', cost: 0, minStage: 1,
    reserveLifePct: 25, wallBlockPct: 5,
    /* 盾墙本身不造成伤害，它的伤害来自「格挡触发的碎石崩裂」——
     * 碎石是**投射物**，所以盾墙也要带上 projectile 标签：
     * 多重投射物 / 投射物加速 镶到盾墙后，作用的对象就是这些碎石。 */
    tags: ['projectile'], projectileNote: '格挡触发的碎石崩裂',
    desc: '释放后进入持续状态：占用 25% 生命上限，格挡几率提高 5%。' +
      '每次成功格挡都会触发「碎石崩裂」：从身前射出碎石轰击前方敌人，' +
      '造成「当前格挡几率% × 攻击力」的攻击伤害 + 25% 生命上限的真实伤害。' +
      '【标签：投射物】碎石属于投射物，可镶「多重投射物」（碎石更多、独立结算更多次）' +
      '与「投射物加速」（碎石飞得更快）。'
  }
];

// 《凡人修仙传》修行体系：武者十境 → 下境界 → 中境界 → 上境界
export const REALM_LIST = [
  '武者一境', '武者二境', '武者三境', '武者四境', '武者五境',
  '武者六境', '武者七境', '武者八境', '武者九境', '武者十境',
  '炼气', '筑基', '结丹', '元婴', '化神',
  '炼虚', '合体', '大乘',
  '真仙', '金仙', '太乙', '大罗（玉仙）', '道祖'
];

export const CULTIVATION_STAGES = [];
REALM_LIST.forEach(function (realm, ri) {
  const subs = realm === '道祖' ? [''] : ['初期', '中期', '后期'];
  subs.forEach(function (sub, si) {
    CULTIVATION_STAGES.push({ realm: realm, sub: sub, realmIndex: ri, subIndex: si });
  });
});

export const MAX_STAGE = CULTIVATION_STAGES.length;
export const TRIBULATION_STAGE = 30; // 武者十境后期（大圆满，需要天地灵物）

/* ============================================================
 * 修仙门派（框架版）
 *   · 目前只有「九玄山」；入门门槛：练气期（stage ≥ 31，即炼气·初期）
 *   · 门派任务：每天一轮，做完在门派窗口里领 门派贡献 / 经验 / 金币
 *   · 门派武学：只能用门派贡献兑换，**不进**技能石 / 灵宝的随机池
 *   · 同门弟子之间不能互相神识锁定（偷袭 / 抢灵脉）——客户端 + 服务端双重拦截
 *   细节（更多门派、门派战、职位等）后续再加，这里先把数据表与规则留出口子。
 * ============================================================ */
export const SECTS = [
  {
    id: 'jiuxuan',
    name: '九玄山',
    color: '#8fd0ff',
    glow: 'rgba(143, 208, 255, .45)',
    minStage: 31,                     // 练气期：炼气·初期 = 第 31 阶
    realmText: '练气期',
    tagline: '九玄环山 · 剑意成阵',
    desc: '九玄山以九道盘旋山路立派，山中剑气常年不散，弟子以剑意引气入体、' +
      '以山势养剑心。入门者须已入练气，方能承受山门剑阵的洗炼。',
    joinHint: '炼气期（练气期）及以上方可拜入',
    /* 门派任务按殿宇下发（每日一轮；track 对应行为埋点）：
     *   visitHall   拜访门派其他殿宇           herb    采集药材（洞府采药）
     *   refine      丹殿炼制丹药               killSameRealm 击败同境界敌人
     *   mine        门派矿山挖矿
     *   kill / cave / share / chat 沿用原有埋点 */
    // 门派武学：price = 需要的门派贡献
    skills: [
      { id: 'jiuxuanSword', name: '九玄剑诀', price: 60, minStage: 31,
        type: 'spell', cost: 8, dmg: [90, 130],
        desc: '九玄剑气三转，对当前敌人造成 90~130 点法术伤害。' },
      { id: 'jiuxuanRift', name: '九玄·裂风斩', price: 150, minStage: 31,
        type: 'spell', cost: 12, dmg: [150, 210],
        desc: '剑气裂风而出，对当前敌人造成 150~210 点法术伤害。' },
      { id: 'jiuxuanSeal', name: '九玄·镇岳印', price: 260, minStage: 31,
        type: 'spell', cost: 16, dmg: [230, 300],
        desc: '引山岳之势为印，对当前敌人造成 230~300 点法术伤害。' }
    ],
    /* 门派修炼法诀（在修炼殿兑换 / 参悟）：
     *   ① 封极法：器炼类秘法，不修灵力、不给自身属性，只按"层"单向增幅某个部位的装备；
     *      触发条件 = 该件装备的核心词缀必须"同源统一"（同一类词缀），否则该件完全不吃加成；
     *      每层只判自己那件装备，互不牵连；第七层（圆满）把整体增幅倍率翻倍。
     *   ② 炼血刀诀：近战武器被动，每次攻击比对双方生命百分比：
     *      你更高 → 追加"差额比例"的额外伤害；你更低 → 按差额比例吸取本次伤害。
     *   注：本作没有独立"腰带"槽，五层・锁腰落在首饰位（jewelry）上。 */
    arts: [
      {
        id: 'fengji', name: '封极法', kind: 'equipment',
        desc: '古器修遗留秘典：弃自身灵力增长，以神魂刻封道锁，挖掘器物本身的极限。' +
          '极偏之法 —— 想凑齐一整套词缀纯净统一的装备，代价巨大。',
        layers: [
          { id: 'fengji1', name: '一层・锁兵', slot: 'weapon', price: 60, boostPct: 20,
            desc: '锁定武器：武器词缀同源统一时才吃增幅（入门根基）。' },
          { id: 'fengji2', name: '二层・锁铠', slot: 'clothes', price: 120, boostPct: 24,
            desc: '锁定胸甲：与一层相互关联，属性类型也要统一才生效。' },
          { id: 'fengji3', name: '三层・锁首', slot: 'helmet', price: 180, boostPct: 28,
            desc: '锁定头盔：头盔词缀统一才生效。' },
          { id: 'fengji4', name: '四层・锁腕', slot: 'gloves', price: 240, boostPct: 32,
            desc: '锁定护腕（手套位）：词缀统一触发加成。' },
          { id: 'fengji5', name: '五层・锁腰', slot: 'jewelry', price: 300, boostPct: 36,
            desc: '锁定腰带（本作落在首饰位）：词缀统一触发加成。' },
          { id: 'fengji6', name: '六层・锁履', slot: 'shoes', price: 360, boostPct: 40,
            desc: '锁定鞋履：六层起六个部位可同时承载增幅，但每件装备各自独立判定。' },
          { id: 'fengji7', name: '七层・封极圆满', slot: null, price: 520, boostPct: 0,
            desc: '前六层全部参悟贯通后方可冲击：所有满足词缀统一条件的装备，增幅倍率整体翻倍。' }
        ]
      },
      {
        id: 'lianxue', name: '炼血刀诀', kind: 'weapon',
        desc: '近战武器被动【炼血】：每次攻击比对双方生命百分比 —— ' +
          '你更高则追加"差额比例"的额外伤害；你更低则按差额比例吸取本次攻击伤害。',
        layers: [
          { id: 'lianxue1', name: '炼血・入门', slot: null, price: 200, boostPct: 0,
            desc: '习得【炼血】：按双方生命百分比差额增伤 / 吸血。' }
        ]
      }
    ],
    /* 山门道路两边的建筑设施：点建筑进内部；每座殿宇下发自己的门派任务 */
    buildings: [
      {
        id: 'hall', name: '大殿', glyph: '殿',
        desc: '门派议事之所。掌门与长老在此论道、颁发门规（内容后续开放）。',
        tasks: [
          { id: 'hallVisit', name: '门规巡查：拜访门派其他殿宇 3 次', track: 'visitHall', target: 3,
            reward: { contribution: 20, xp: 260, gold: 180 } }
        ]
      },
      {
        id: 'alchemy', name: '丹殿', glyph: '丹',
        desc: '炼丹房。可用门派贡献兑换丹药；弟子也在此接"采集药材"的门派任务。',
        tasks: [
          { id: 'alchemyRefine', name: '开炉炼丹：炼制 3 次丹药', track: 'refine', target: 3,
            reward: { contribution: 25, xp: 360, gold: 240 } },
          { id: 'alchemyHerb', name: '采集药材：在洞府中采集 10 份药材', track: 'herb', target: 10,
            reward: { contribution: 30, xp: 400, gold: 260 } },
          { id: 'alchemyCave', name: '药源探查：完成 1 次洞府探险', track: 'cave', target: 1,
            reward: { contribution: 20, xp: 300, gold: 180 } }
        ]
      },
      {
        id: 'cultivation', name: '修炼殿', glyph: '修',
        desc: '静修之地。在此打坐吐纳可把灵力（法力）补满；门中弟子也在此领"同境切磋"的任务。',
        tasks: [
          { id: 'cultSameRealm', name: '同境切磋：击败 15 名同境界的敌人', track: 'killSameRealm', target: 15,
            reward: { contribution: 35, xp: 500, gold: 320 } },
          { id: 'cultKill', name: '实战磨砺：击败 20 名敌人（不限境界）', track: 'kill', target: 20,
            reward: { contribution: 20, xp: 320, gold: 200 } }
        ]
      },
      {
        id: 'reception', name: '接引殿', glyph: '引',
        desc: '接引新弟子、下发门派任务之处，也负责往矿山派发差事。',
        tasks: [
          { id: 'recShare', name: '同门共进：分享 1 次装备到世界频道', track: 'share', target: 1,
            reward: { contribution: 10, xp: 120, gold: 100 } },
          { id: 'recChat', name: '传音入密：在世界频道发言 1 次', track: 'chat', target: 1,
            reward: { contribution: 10, xp: 100, gold: 80 } }
        ]
      },
      {
        id: 'mine', name: '门派矿山', glyph: '矿',
        desc: '门派矿脉。点击「开凿矿石」即可挖矿，产出矿石材料；此处差事多为采掘。',
        tasks: [
          { id: 'mineDig', name: '矿脉开采：在门派矿山挖矿 10 次', track: 'mine', target: 10,
            reward: { contribution: 30, xp: 420, gold: 280 } }
        ]
      }
    ]
  }
];

// 门派武学扁平表（技能查找用；刻意不放进 ACTIVE_SKILL_POOL，避免被技能石随机抽到）
export const SECT_SKILLS = SECTS.reduce(function (acc, s) { return acc.concat(s.skills || []); }, []);

/* ============================================================
 * 阵营 · 武者大圆满的「命运分叉点」
 *   世界观底层逻辑：
 *     天道   = 外来统治势力（把本世界当资源收割场）
 *     世界BOSS = 这方天地的原生本土生灵（被天道污名化成「妖魔」）
 *     仙人   = 归顺外来天道的本土修行者（天道的执行者）
 *     逆仙   = 接受本土世界BOSS 力量、反抗外来天道的本土修行者
 *   两个选项本质不是"拿 buff"，而是**选择效忠对象**：
 *     献祭灵性 → 仙途：向域外天道投名，换法则祝福
 *     接纳辟佑 → 逆仙：承本土本源，此后每次突破引神雷淬炼肉身
 *   当前版本只实现「武者大圆满」这一段剧情演绎，
 *   后续的境界逻辑（雷劫伤害 / 法则技能加持 / 身份隐匿）留到后续版本。
 *   hidden 是"后期才解锁"的隐藏背景，本次不展示。
 * ============================================================ */
export const FACTION_INTRO =
  '天地灵物在你手中震颤，其中封存着那只世界 BOSS 残存的灵性。你感知到两条截然不同的道路。';

export const FACTIONS = [
  {
    id: 'xian',
    name: '仙途',
    title: '献祭 BOSS 灵性，祈求天道祝福，踏入仙途',
    desc: '将这份本土生灵的灵性献祭于天道，得到天道认可。往后修行，天道将赐予你法则祝福，技能获得法则加持。',
    // 后期才解锁：你将成为天道的执行者，参与肃清本土原生生灵。
    hidden: '你将成为天道的执行者，参与肃清本土原生生灵。',
    color: '#a8d1ff',
    glyph: '仙',
    result: '你献祭了那份本土灵性。天道认可了你的叩首，一缕法则之力自九天垂落，溶于你的经脉。'
  },
  {
    id: 'nixian',
    name: '逆仙',
    title: '传承神灵，天雷淬体，逆伐仙途',
    desc: '接纳世界 BOSS 残存的辟佑之力，依靠这片大地本身的力量突破。天道不再认可你的修行，往后突破必引神雷淬炼己身，肉身之力大幅增长。',
    // 后期才解锁：你将成为本土的守护者，行走在天道的阴影之中，身份可隐匿。
    hidden: '你将成为本土的守护者，行走在天道的阴影之中，身份可隐匿。',
    color: '#ff9d6a',
    glyph: '逆',
    result: '你接住了那份残存的辟佑。大地深处传来一声低沉的回应，天道在你的命数上划去了一笔。'
  }
];

export const SAVE_KEY = 'homeward.save.v1';

/* ============================================================
 * 开场 CG（幻灯片 · 静态插图蒙太奇）
 *   位置：登录校验成功之后、加载并进入主场景之前（见 main.js 的 cgGateThenStart）
 *   表现：每张静态插画 + 缓慢推拉（Ken Burns）+ 图间淡入淡出，
 *         旁白逐行浮现、底部一行字幕，随时可跳过。
 *   art：插画文件（1280×720 最合适）。文件缺失时会用程序绘制的剪影分镜兜底，
 *        所以现在没有美术也能跑；把图放到 public/assets/img/cg/ 下即自动替换。
 *   基调：苍凉史诗，不做单纯善恶评判，冲突根源是「域外入侵者 vs 世界原生生灵」。
 * ============================================================ */
export const CG_ART_DIR = 'assets/img/cg/';
export const CG_SLIDES = [
  {
    id: 'cg1', art: 'cg_1.png', title: '远古',
    dur: 7.5
  },
  {
    id: 'cg2', art: 'cg_2.png', title: '巨变',
    dur: 7.5
  },
  {
    id: 'cg3', art: 'cg_3.png', title: '讨伐',
    dur: 8
  },
  {
    /* 只保留 4 张（原 1 / 2 / 3 / 5）：
     *   id 保留原编号 —— 程序绘制的分镜按 id 分发（cg5 还是"仙途"那一张）；
     *   art 改成连续编号 —— 以后要放正式插画，按 cg_1.png ~ cg_4.png 丢进 public/assets/img/cg/ 即可。 */
    id: 'cg5', art: 'cg_4.png', title: '择道',
    dur: 9
  }
];

/* ============================================================
 * 时装（外观）
 * ============================================================ */
/* ============================================================
 *   每个世界BOSS 一件固定时装，只从"开启世界BOSS宝箱"掉落，
 *   单箱 0.05% 概率（见 main.js 的 WB_COSTUME_PCT）。
 *   穿上后不加任何属性；**只在角色窗口的「时装展示槽」里显示**。
 *   （曾经做过"贴到角色头上"的实时贴图，多次调试仍然容易错位，
 *     已按需求移除：冒险画面 / 立绘上都不再绘制时装。）
 *   例外：配了 phantom 的时装，穿上后会在**玩家身后**浮起该世界BOSS 的虚影
 *   （半透明魂体、比本体小、紧贴背后，见 main.js 的 drawBossPhantom）。
 *   slot：目前只做头部（head），以后可扩 body / weapon 等。
 * ============================================================ */
export const COSTUME_SLOTS = [
  { id: 'head', label: '头饰', icon: '冠' }
];

export const COSTUMES = [
  /* 目前**没有上线中的时装**：森特欧斯面具是当初的测试道具，
   * 虚影效果验收完之后按需求下架了（不再从世界BOSS宝箱掉落、也不再自动发给测试账号）。
   * 下架 ≠ 删功能：时装槽、虚影绘制、击杀充能那套机制都还在，加一条定义就能用。
   *
   * 以后给某个世界BOSS 加时装，在这里加一条即可：
   *   {
   *     id: 'xxxMask', slot: 'head', bossId: '<世界BOSS id>', name: '…', icon: '面',
   *     art: 'assets/img/costume/<文件>.png',      // 参考 tools/import_costume_art.cjs 裁边压缩
   *     phantom: '<世界BOSS id>',                   // 有这行 = 「世界BOSS 时装」：身后浮虚影 + 吃击杀充能
   *     desc: '…', drop: '世界BOSS「…」的宝箱（0.05%）'
   *   }
   *   掉落概率见 main.js 的 WB_COSTUME_PCT；充能机制见 main.js 的 COSTUME_CHARGE_* 。
   * 开发期想拿一件来试：控制台 __debugGiveTestCostume('<id>')（只在本地/内网生效）。 */
];

/* ============================================================
 * 副本（入侵）· 每日轮换
 *   每周 7 天各开放一个副本，全天开放、当天 00:00（东八区上海时间）轮换，
 *   7 个副本在一周里各占一天，不重复、不冲突。
 *   ⚠ 当前版本只实现 UI（窗口 + 排期展示），玩法留到后续版本。
 * ============================================================ */
export const DUNGEONS = [
  {
    id: 'rift', name: '裂隙入侵', weekday: 1, accent: '#9fd4ff',
    reward: '裂隙专属特殊物品',
    lines: [
      '击杀敌人掉落「裂隙碎片」',
      '集满 300 颗碎片即可激活裂隙石',
      '开启传奇BOSS特殊地图并挑战',
      '通关获得裂隙专属特殊物品'
    ]
  },
  {
    id: 'cataclysm', name: '灾变入侵', weekday: 2, accent: '#ff8f72',
    reward: '灾变身体部件（可镶嵌在装备上）',
    lines: [
      '击杀被灾厄入侵的敌人，掉落其碎片',
      '碎片凑齐一组后激活灾变',
      '击杀灾变敌人获得它的身体部分',
      '把身体部分镶嵌在当前装备上'
    ]
  },
  {
    id: 'fog', name: '迷雾入侵', weekday: 3, accent: '#b9a6ff',
    reward: '迷雾专属特殊物品',
    lines: [
      '击杀敌人掉落「迷雾碎片」',
      '集满 300 颗碎片即可激活迷雾石',
      '开启传奇BOSS特殊地图并挑战',
      '通关获得迷雾专属特殊物品'
    ]
  },
  {
    id: 'exorcism', name: '驱灵入侵', weekday: 4, accent: '#7dff9b',
    reward: '驱灵专属特殊物品',
    lines: [
      '击杀敌人掉落「驱灵碎片」',
      '集满 300 颗碎片即可激活驱灵石',
      '开启传奇BOSS特殊地图并挑战',
      '通关获得驱灵专属特殊物品'
    ]
  },
  {
    id: 'abyss', name: '深渊入侵', weekday: 5, accent: '#c08cff',
    reward: '为装备追加 1 条额外随机词缀',
    lines: [
      '击杀敌人有几率掉落「深渊物品」',
      '深渊物品可以给装备附加属性',
      '为一件装备添加 1 条额外随机词缀'
    ]
  },
  {
    id: 'essence', name: '精华入侵', weekday: 6, accent: '#ffd76a',
    reward: '为装备追加 1 条额外随机词缀',
    lines: [
      '击杀敌人有几率掉落「精华物品」',
      '精华物品可以给装备附加属性',
      '为一件装备添加 1 条额外随机词缀'
    ]
  },
  {
    id: 'exile', name: '流亡者入侵', weekday: 7, accent: '#ffb24d',
    reward: '流亡者身上的装备',
    lines: [
      '流亡者会随机出现在冒险路上',
      '击杀流亡者即可夺取他身上的物品',
      '掉落品质与流亡者携带的装备一致'
    ]
  }
];

/* ============================================================
 * 玩家属性点（自由分配）
 *   每提升一个境界（等级）获得 ATTR_POINTS_PER_LEVEL 点
 *   力量 / 敏捷 / 智力 的加成写在 note 里，角色窗口会原样展示
 * ============================================================ */
export const ATTR_POINTS_PER_LEVEL = 1;

export const ATTR_DEFS = [
  { id: 'str', label: '力量', note: '每点 +5 生命上限；力量总额还会加强「气血反弹」' },
  { id: 'agi', label: '敏捷', note: '每点 +1% 攻击速度、+0.6% 移动速度' },
  { id: 'int', label: '智力', note: '每点 +2 灵力上限（施放法术消耗灵力，灵力同时提高宠物伤害）' }
];

/* ============================================================
 * 马路上的遗迹洞府
 *   绿色 / 蓝色：随时可以进入与离开
 *   金色：需要「金色洞府令牌」（只能用活力兑换）才能进入
 *   敌人数量基准（野外一段路按 CAVE_WILD_BASE 个敌人算）：
 *     绿色 = 1.5 × 野外、蓝色 = 2 × 绿色、金色 = 2 × 蓝色
 * ============================================================ */
export const CAVE_WILD_BASE = 8;          // 野外基准敌人数量（仅用于说明比例）
export const CAVE_DEFS = [
  {
    id: 'green', name: '绿色洞府', color: '#5ce08a', glow: 'rgba(92, 224, 138, .55)',
    enemies: Math.round(CAVE_WILD_BASE * 1.5), // 12
    token: false, weight: 55,
    note: '随时可以进入，敌人密度比野外更高',
    tiers: ['common', 'common', 'magic']
  },
  {
    id: 'blue', name: '蓝色洞府', color: '#5fb0ff', glow: 'rgba(95, 176, 255, .55)',
    enemies: Math.round(CAVE_WILD_BASE * 1.5) * 2, // 24（绿色 ×2）
    token: false, weight: 33,
    note: '随时可以进入，敌人数量是绿色洞府的 2 倍',
    tiers: ['common', 'magic', 'magic', 'elite']
  },
  {
    id: 'gold', name: '金色洞府', color: '#ffd76a', glow: 'rgba(255, 215, 106, .6)',
    enemies: Math.round(CAVE_WILD_BASE * 1.5) * 2 * 2, // 48（蓝色 ×2）
    token: true, weight: 12,
    note: '需要金色洞府令牌才能进入，敌人数量是蓝色洞府的 2 倍',
    tiers: ['magic', 'elite', 'elite', 'legend']
  }
];
export const CAVE_TOKEN_COST = 10;        // 活力兑换金色洞府令牌所需活力（每次进金色洞府消耗 1 枚）
// 洞府里战斗时，附近敌人加入战斗（多对一）的几率
export const CAVE_JOIN_CHANCE = 0.35;
export const CAVE_JOIN_MAX = 3;           // 一场战斗最多同时面对几个敌人

/* ============================================================
 * 活动 · 临时公共副本
 *   每 12 小时（上海时间 00:00 / 12:00）随机开启 1~4 个临时副本，
 *   每个只存在 60 分钟；到点后还留在里面的玩家会被传送回公路。
 *   因为它是"公共地图"，进去后能看到其他玩家的虚影在同一张地图里打怪。
 *
 *   境界限制：每个副本对应一个境界，**更高境界的玩家进不去**
 *   （maxStage = 该境界最后一个小阶段，升上去之后就进不来了）。
 *     · 武者一境~武者十境后期 → stage 1~30
 *     · 炼气 初期/中期/后期     → stage 31~33
 *     · 结丹 初期/中期/后期     → stage 37~39
 *     · 元婴 初期/中期/后期     → stage 40~42
 * ============================================================ */
export const ACTIVITY_PERIOD_HOURS = 12;   // 每 12 小时换一批
export const ACTIVITY_LIFETIME_MIN = 60;   // 每个临时副本存在 60 分钟

/* ⚠ 仅测试用：这些临时副本「常开 + 不限境界」，随时可以进去验收（窗口里会标「测试」）。
 * 正式服要恢复"按 12 小时排期 + 境界限制"时，把数组清空即可：
 *   export const ACTIVITY_TEST_OPEN = [];
 * 也可以在控制台临时开关：__debugActivityTestOpen('actRottenAltar', false) */
export const ACTIVITY_TEST_OPEN = ['actRottenAltar'];   // 朽木祭坛（武者境）

/* 临时副本里最常见的"入侵材料"（对应副本窗口里的 7 种入侵）。
 * 天地灵物属于极品材料，**只在第 3 层出现 1 个**，不进这个随机池。
 * prefix：镶嵌到装备上时给装备加的前缀（例如 迷雾*长弓），外加 1 条随机词缀。 */
export const INVASION_MATERIALS = [
  { id: 'riftShard', name: '裂隙碎片', prefix: '裂隙', icon: '裂', color: '#9fd4ff',
    detail: '裂隙入侵的产物 · 可镶嵌：为装备加前缀「裂隙」并追加 1 条随机词缀' },
  { id: 'cataclysmPart', name: '灾变残骸', prefix: '灾变', icon: '灾', color: '#ff8f72',
    detail: '灾变入侵的产物 · 可镶嵌：为装备加前缀「灾变」并追加 1 条随机词缀' },
  { id: 'fogShard', name: '迷雾碎片', prefix: '迷雾', icon: '雾', color: '#b9a6ff',
    detail: '迷雾入侵的产物 · 可镶嵌：为装备加前缀「迷雾」并追加 1 条随机词缀' },
  { id: 'exorcismShard', name: '驱灵碎片', prefix: '驱灵', icon: '驱', color: '#7dff9b',
    detail: '驱灵入侵的产物 · 可镶嵌：为装备加前缀「驱灵」并追加 1 条随机词缀' },
  { id: 'abyssItem', name: '深渊物品', prefix: '深渊', icon: '渊', color: '#c08cff',
    detail: '深渊入侵的产物 · 可镶嵌：为装备加前缀「深渊」并追加 1 条随机词缀' },
  { id: 'essenceItem', name: '精华物品', prefix: '精华', icon: '精', color: '#ffd76a',
    detail: '精华入侵的产物 · 可镶嵌：为装备加前缀「精华」并追加 1 条随机词缀' },
  { id: 'exileItem', name: '流亡者遗物', prefix: '流亡者', icon: '流', color: '#ffb24d',
    detail: '流亡者入侵的产物 · 可镶嵌：为装备加前缀「流亡者」并追加 1 条随机词缀' }
];

/* ============================================================
 * 装备回收（拆解）
 *   背包里勾选品级 → 一键回收：直接按商店价卖掉，同时有较低几率
 *   额外掉一份「对应稀有度」的材料（白/蓝/金 各自一种）。
 *   传奇（灵宝）不在回收列表里，避免误拆。
 * ============================================================ */
export const RECYCLE_TIERS = [
  { id: 'common', label: '白色装备', grade: '普通', color: '#d8d3c2' },
  { id: 'magic', label: '蓝色装备', grade: '魔法', color: '#67b6ff' },
  { id: 'rare', label: '金色装备', grade: '稀有', color: '#ffd265' }
];

/* 回收产出的材料：名字 / 图标 / 颜色 / 说明都跟着品级走。
 * 注意 type 仍是 'material'（和普通制作材料同族，能叠加、能进背包），
 * 但不带 materialId —— 这样不会混进「镶嵌材料」的库存里。 */
export const RECYCLE_MATERIALS = {
  common: { name: '凡铁残片', icon: '铁', color: '#d8d3c2', rarityName: '普通材料',
    detail: '回收白色装备拆出的材料' },
  magic: { name: '灵纹碎屑', icon: '纹', color: '#67b6ff', rarityName: '魔法材料',
    detail: '回收蓝色装备拆出的材料' },
  rare: { name: '鎏金残髓', icon: '鎏', color: '#ffd265', rarityName: '稀有材料',
    detail: '回收金色装备拆出的材料' }
};

/* 回收时额外掉材料的几率（每种品级各自独立判定一次） */
export const RECYCLE_MATERIAL_CHANCE = 0.03;

export const ACTIVITY_DUNGEONS = [
  {
    id: 'actRottenAltar', name: '朽木祭坛', realm: '武者境', maxStage: 30,
    color: '#c8a35a', glow: 'rgba(200, 163, 90, .5)',
    enemies: 18, tiers: ['common', 'common', 'magic'],
    enemyStage: 20, minEnemyStage: 1, rewardTier: 'green',
    note: '枯木盘绕的旧祭坛 · 武者境可入'
  },
  {
    id: 'actMiasmaCrypt', name: '瘴根地窟', realm: '炼气境', maxStage: 33,
    color: '#7fe08a', glow: 'rgba(127, 224, 138, .5)',
    enemies: 24, tiers: ['common', 'magic', 'magic', 'elite'],
    enemyStage: 32, minEnemyStage: 31, rewardTier: 'blue',
    note: '瘴气从根须间渗出 · 炼气境可入'
  },
  {
    id: 'actToxicRift', name: '毒壤裂隙', realm: '结丹境', maxStage: 39,
    color: '#c07fe0', glow: 'rgba(192, 127, 224, .5)',
    enemies: 30, tiers: ['magic', 'magic', 'elite'],
    enemyStage: 38, minEnemyStage: 37, rewardTier: 'gold',
    note: '毒壤裂开的深渊 · 结丹境可入'
  },
  {
    id: 'actGhostTemple', name: '幽根圣殿', realm: '元婴境', maxStage: 42,
    color: '#7fb6ff', glow: 'rgba(127, 182, 255, .5)',
    enemies: 36, tiers: ['magic', 'elite', 'elite', 'legend'],
    enemyStage: 41, minEnemyStage: 40, rewardTier: 'gold',
    note: '幽根盘成的圣殿 · 元婴境可入'
  }
];

/* ============================================================
 * 图鉴阶级：普通 / 魔法 / 稀有 / 传奇 / 暗金
 *   rate    单抽出现几率（%）
 *   rateTen 十连每一抽的出现几率（%）：稀有 / 传奇 / 暗金 都被抬高
 *   comp    抽到重复藏品时的金币补偿
 * ============================================================ */
export const CODEX_TIERS = [
  {
    id: 'common', name: '普通', color: '#d8d3c2', rate: 58.52, rateTen: 61.00, comp: 20,
    note: '路边摊随手就能收来的凡品'
  },
  {
    id: 'magic', name: '魔法', color: '#67b6ff', rate: 36.38, rateTen: 37.95, comp: 50,
    note: '带一点灵性的小玩意'
  },
  {
    id: 'rare', name: '稀有', color: '#ffd265', rate: 5, rateTen: 1, comp: 120,
    note: '难得一见的地方名物'
  },
  {
    id: 'legend', name: '传奇', color: '#ff8f4d', rate: 0.1, rateTen: 0.05, comp: 400,
    note: '史册里留有名字的宝物'
  },
  {
    // 暗金：不参与抽卡，也不会被保底抽出，只能靠活动等其它方式获得（暂未开放）
    id: 'unique', name: '暗金', color: '#c58bff', rate: 0, rateTen: 0, comp: 1200,
    gacha: false,
    source: '活动等方式获取（暂未开放）',
    note: '世上仅此一件，得之即为传承'
  }
];

/* ============================================================
 * 图鉴藏品（收集后提供永久增幅属性）
 *   tier: common / magic / rare / legend / unique
 *   bonus: 属性键 -> 数值，键名与装备词缀一致
 * ============================================================ */
export const CODEX_ITEMS = [
  // ---------- 普通 ----------
  {
    id: 'cm_pot', tier: 'common', name: '粗陶护身符', icon: '符', type: '饰品',
    desc: '村口陶窑烧出的护身符，据说能挡一次风寒。',
    bonus: { hp: 8 }
  },
  {
    id: 'cm_hammer', tier: 'common', name: '铁匠锤', icon: '锤', type: '武器',
    desc: '抡起来砸核桃比砸人顺手。',
    bonus: { atk: 3 }
  },
  {
    id: 'cm_hat', tier: 'common', name: '猎户皮帽', icon: '帽', type: '头盔',
    desc: '猎户进山前都会戴上一顶。',
    bonus: { armor: 5 }
  },
  {
    id: 'cm_wrap', tier: 'common', name: '粗布足袋', icon: '袋', type: '鞋子',
    desc: '缠上它，走在滚烫的沙上也稳当些。',
    bonus: { speedPct: 2 }
  },
  {
    id: 'cm_dagger', tier: 'common', name: '木柄短匕', icon: '匕', type: '武器',
    desc: '削木头、剔骨头都很称手。',
    bonus: { atk: 2, critChance: 1 }
  },
  {
    id: 'cm_bag', tier: 'common', name: '干粮布袋', icon: '粮', type: '道具',
    desc: '里头还剩半块硬饼。',
    bonus: { hp: 6, lifeRegen: 1 }
  },
  {
    id: 'cm_coins', tier: 'common', name: '铜钱串', icon: '钱', type: '饰品',
    desc: '外圆内方，压得住邪气。',
    bonus: { res: 3 }
  },
  {
    id: 'cm_basket', tier: 'common', name: '药农背篓', icon: '篓', type: '道具',
    desc: '背篓里总混着一股草药味。',
    bonus: { mp: 6 }
  },

  // ---------- 魔法 ----------
  {
    id: 'mg_jade', tier: 'magic', name: '青纹玉佩', icon: '玉', type: '饰品',
    desc: '玉纹如水，贴着心口时微微发凉。',
    bonus: { hp: 18, res: 4 }
  },
  {
    id: 'mg_bow', tier: 'magic', name: '游侠轻弓', icon: '弓', type: '武器',
    desc: '弓身轻若无物，开弦却劲道十足。',
    bonus: { atk: 6, atkSpeed: 4 }
  },
  {
    id: 'mg_bracer', tier: 'magic', name: '寒铁护腕', icon: '腕', type: '护腕',
    desc: '铁面结霜，碰一下能划破皮。',
    bonus: { armor: 18, thorns: 6 }
  },
  {
    id: 'mg_staff', tier: 'magic', name: '聚灵木杖', icon: '杖', type: '武器',
    desc: '杖头聚着一小团不散的水汽。',
    bonus: { mp: 16, critChance: 2 }
  },
  {
    id: 'mg_boots', tier: 'magic', name: '疾风布靴', icon: '靴', type: '鞋子',
    desc: '落地极轻，走夜路不惊犬。',
    bonus: { speedPct: 4, hp: 12 }
  },
  {
    id: 'mg_glove', tier: 'magic', name: '铜环护手', icon: '手', type: '手套',
    desc: '环上刻着谁也不认得的古符。',
    bonus: { atk: 4, atkSpeed: 3 }
  },

  // ---------- 稀有 ----------
  {
    id: 'rr_helm', tier: 'rare', name: '赤炎战盔', icon: '盔', type: '头盔',
    desc: '盔顶余温未散，像刚离炉膛。',
    bonus: { hp: 30, armor: 25 }
  },
  {
    id: 'rr_ring', tier: 'rare', name: '幽蓝蛇戒', icon: '戒', type: '饰品',
    desc: '戒面盘着一条会眨眼的青蛇。',
    bonus: { critChance: 4, critDamage: 15 }
  },
  {
    id: 'rr_charm', tier: 'rare', name: '玄水石符', icon: '符', type: '饰品',
    desc: '浸在井水里三天三夜也不会湿。',
    bonus: { res: 6, chaosRes: 4 }
  },
  {
    id: 'rr_sword', tier: 'rare', name: '断岳长剑', icon: '剑', type: '武器',
    desc: '剑脊有道旧裂痕，是从前劈山留下的。',
    bonus: { atk: 10, critDamage: 12 }
  },
  {
    id: 'rr_mail', tier: 'rare', name: '荆棘重甲', icon: '甲', type: '衣服',
    desc: '甲面生满倒刺，抱一下便知厉害。',
    bonus: { thorns: 14, armor: 30 }
  },
  {
    id: 'rr_boots', tier: 'rare', name: '疾影长靴', icon: '靴', type: '鞋子',
    desc: '靴底磨得极薄，却从未穿透过。',
    bonus: { speedPct: 7, atkSpeed: 6 }
  },

  // ---------- 传奇 ----------
  {
    id: 'leg_mirror', tier: 'legend', name: '天罡护心镜', icon: '镜', type: '护甲',
    desc: '镜面映出杀意，可挡下最致命的一击。',
    bonus: { armor: 60, blockPct: 6 }
  },
  {
    id: 'leg_boots', tier: 'legend', name: '玄铁不灭靴', icon: '靴', type: '鞋子',
    desc: '踏沙不留痕，行过万里而不损分毫。',
    bonus: { speedPct: 8, hp: 30 }
  },
  {
    id: 'leg_pearl', tier: 'legend', name: '紫霄雷珠', icon: '珠', type: '法宝',
    desc: '珠中藏着一道未落下的天雷。',
    bonus: { critChance: 8, critDamage: 25 }
  },
  {
    id: 'leg_gloves', tier: 'legend', name: '金刚伏魔手', icon: '手', type: '手套',
    desc: '据说是降魔者以自身骨血炼成的手套。',
    bonus: { atk: 8, atkSpeed: 10 }
  },
  {
    id: 'leg_robe', tier: 'legend', name: '万木归元甲', icon: '甲', type: '衣服',
    desc: '以万年灵木编织，生机不绝。',
    bonus: { hp: 70, armor: 40 }
  },
  {
    id: 'leg_ring', tier: 'legend', name: '星辰碎影戒', icon: '戒', type: '饰品',
    desc: '戴上它，连星辰的坠落都慢了一瞬。',
    bonus: { critReduce: 12, res: 5 }
  },
  {
    id: 'leg_bracer', tier: 'legend', name: '血魂护腕', icon: '腕', type: '护腕',
    desc: '护腕上凝着历代主人的血魂，反噬一切来犯者。',
    bonus: { thorns: 18, strength: 15 }
  },

  // ---------- 暗金 ----------
  {
    id: 'uni_sword', tier: 'unique', name: '太虚斩仙剑', icon: '剑', type: '武器',
    desc: '传说中斩落过仙人的一剑，锋芒所指，虚空生裂。',
    bonus: { atk: 14, critChance: 4 }
  },
  {
    id: 'uni_banner', tier: 'unique', name: '混沌吞天幡', icon: '幡', type: '法宝',
    desc: '幡面漆黑如夜，能吞噬一切元素之力。',
    bonus: { res: 8, chaosRes: 6, mp: 20 }
  },
  {
    id: 'uni_jade', tier: 'unique', name: '九幽血玉', icon: '玉', type: '饰品',
    desc: '九幽深处的血玉，握之如握一颗跳动的心脏。',
    bonus: { hp: 90, lifeRegen: 5 }
  },
  {
    id: 'uni_dragon', tier: 'unique', name: '烛龙逆鳞甲', icon: '甲', type: '衣服',
    desc: '以烛龙逆鳞串成，触之如触滚烫的岩浆。',
    bonus: { hp: 120, armor: 80, thorns: 25 }
  },
  {
    id: 'uni_seal', tier: 'unique', name: '太一神雷印', icon: '印', type: '法宝',
    desc: '印面雷纹流转，落下时天地俱寂。',
    bonus: { critChance: 10, critDamage: 40, mp: 30 }
  }
];

// 老存档的图鉴 id 迁移（原「神级」并入「暗金」）
export const CODEX_LEGACY_IDS = {
  god_sword: 'uni_sword',
  god_banner: 'uni_banner',
  god_jade: 'uni_jade'
};

// 图鉴属性键 -> 中文名（用于面板显示）
export const STAT_LABELS = {
  hp: '最大生命', armor: '护甲', atk: '攻击伤害', speedPct: '移动速度', mp: '最大灵力',
  res: '全元素抗性', thorns: '荆棘伤害', blockPct: '格挡几率', strength: '力量',
  chaosRes: '混沌抗性', critReduce: '暴击伤害降低', armorPct: '护甲提高',
  lifeRegen: '生命每回合再生', atkSpeed: '攻击速度', critChance: '暴击率', critDamage: '暴击伤害'
};

/* ============================================================
 * 抽卡系统：消耗金币，按阶级概率抽取图鉴藏品
 *   单抽 / 十连 用两套概率表（CODEX_TIERS 的 rate / rateTen）：
 *   十连会整体抬高 稀有 / 传奇 / 暗金 的几率，并额外保底一件稀有及以上。
 * ============================================================ */
export const GACHA_SINGLE_COST = 500;   // 单抽金币
export const GACHA_TEN_COST = 5000;     // 十连金币
export const GACHA_PITY_MAX = 50;       // 50 抽内必出传奇（暗金不参与保底）
export const GACHA_TEN_MIN_TIER = 'rare'; // 十连保底的最低阶级

// 十连限流：一次十连里 稀有 最多 2 件，传奇 + 暗金 合计最多 1 件
export const GACHA_TEN_CAPS = { rare: 2, legend: 1 };

/* ============================================================
 * 装备词缀数值区间（按 10 级为一档）
 *
 * 规则：装备等级决定"能出现的最高档位"，但每次都从 0 档开始随机。
 *   1~10 级装备：只能出第 1 档（1-10 级区间）
 *   15 级装备   ：可以出第 1 档或第 2 档（1-20 级区间）
 *   100 级装备  ：可以出第 1~10 档（也可能六条全是最低档 → 看运气）
 * ============================================================ */
export const AFFIX_BAND_SIZE = 10;

// 盾牌专属属性库（参考 PoE2：生命 / 护甲 / 荆棘 / 格挡 / 力量 / 元素抗性 / 混沌抗性 / 暴击伤害降低）
export const SHIELD_AFFIXES = [
  {
    key: 'hp',
    label: '最大生命 +{v}',
    bands: [[5, 12], [12, 22], [22, 35], [35, 50], [50, 70], [70, 95], [95, 125], [125, 160], [160, 200], [200, 260]]
  },
  {
    key: 'armor',
    label: '护甲 +{v}',
    bands: [[6, 14], [14, 26], [26, 42], [42, 62], [62, 88], [88, 120], [120, 160], [160, 210], [210, 270], [270, 340]]
  },
  {
    key: 'thorns',
    label: '荆棘伤害 +{v}',
    bands: [[2, 5], [5, 9], [9, 14], [14, 20], [20, 28], [28, 38], [38, 50], [50, 65], [65, 82], [82, 105]]
  },
  {
    key: 'blockPct',
    label: '格挡几率 +{v}%',
    bands: [[2, 4], [3, 6], [5, 8], [6, 10], [8, 12], [10, 14], [12, 17], [14, 20], [17, 24], [20, 28]]
  },
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[3, 7], [6, 12], [10, 18], [15, 25], [20, 32], [26, 40], [32, 48], [40, 58], [48, 70], [58, 85]]
  },
  {
    key: 'res',
    label: '全元素抗性 +{v}%',
    bands: [[2, 5], [4, 8], [6, 11], [8, 14], [10, 17], [12, 20], [14, 24], [16, 28], [18, 32], [20, 36]]
  },
  {
    key: 'chaosRes',
    label: '混沌抗性 +{v}%',
    bands: [[1, 3], [2, 5], [3, 7], [4, 9], [5, 11], [6, 13], [7, 15], [8, 17], [10, 20], [11, 23]]
  },
  {
    key: 'critReduce',
    label: '暴击伤害降低 {v}%',
    bands: [[2, 4], [3, 6], [5, 8], [6, 10], [8, 12], [10, 15], [12, 18], [14, 21], [16, 24], [18, 28]]
  },
  /* ---------- 盾牌新增：减伤 + 吸纳（Recoup） ----------
   *   受到的伤害降低 X%（叠加进护甲/抗性之外的额外减伤）
   *   将所受伤害的 X% 吸纳为生命（4 秒内逐跳回满，参考流放之路的 Recoup） */
  {
    key: 'dmgReducePct',
    label: '受到的伤害降低 {v}%',
    bands: [[3, 5], [4, 6], [5, 7], [6, 8], [7, 9], [8, 10], [9, 11], [10, 12], [11, 13], [12, 15]]
  },
  {
    key: 'recoupPct',
    label: '将所受伤害的 {v}% 吸纳为生命',
    bands: [[2, 3], [2, 3], [3, 4], [3, 5], [4, 5], [4, 6], [5, 6], [5, 7], [6, 7], [6, 8]]
  },
  /* ---------- 盾牌新增：物理减伤 / 抗性上限 / 格挡上限 ----------
   *   物理伤害减免：3%~7%（按等级档位从 3 爬到 7）
   *   最大元素抗性上限：+1%~+5%（把"全元素抗性"的上限往上抬）
   *   格挡几率上限：+1%~+5%（把格挡的上限往上抬 —— 只有盾牌能出） */
  {
    key: 'physReducePct',
    label: '物理伤害减免 {v}%',
    bands: [[3, 4], [3, 4], [4, 5], [4, 5], [5, 5], [5, 6], [5, 6], [6, 7], [6, 7], [7, 7]]
  },
  {
    key: 'resCapPct',
    label: '最大元素抗性上限 +{v}%',
    bands: [[1, 1], [1, 2], [1, 2], [2, 2], [2, 3], [2, 3], [3, 4], [3, 4], [4, 5], [4, 5]]
  },
  {
    key: 'blockCapPct',
    label: '格挡几率上限 +{v}%',
    bands: [[1, 1], [1, 2], [1, 2], [2, 3], [2, 3], [3, 3], [3, 4], [4, 4], [4, 5], [5, 5]]
  }
];

// 其他部位/武器共用的通用词缀（数值仍按等级成长）
export const GENERIC_AFFIXES = [
  { key: 'hp', label: '最大生命 +{v}' },
  { key: 'armor', label: '护甲 +{v}' },
  { key: 'atk', label: '攻击伤害 +{v}' },
  { key: 'speedPct', label: '移动速度提高 {v}%' },
  { key: 'mp', label: '最大灵力 +{v}' },
  { key: 'res', label: '全元素抗性 +{v}%' },
  { key: 'thorns', label: '荆棘伤害 +{v}' }
];

/* ============================================================
 * 武器词缀库（长弓 / 短剑 / 爪 / 法杖）
 *   只保留"贴武器"的属性：
 *     生命 / 护甲 / 攻击伤害 / 最大灵力
 *     + 三维：力量 / 敏捷 / 智力
 *     + 攻击速度 / 暴击率 / 暴击伤害
 *   不含：全元素抗性、移动速度、荆棘（那几条属于防具 / 盾牌）
 *   注意：atk 故意不写 bands —— 走 rollOneMod 里那条按武器种类 / 等级线性成长的公式。
 * ============================================================ */
export const WEAPON_AFFIXES = [
  { key: 'hp', label: '最大生命 +{v}' },
  { key: 'armor', label: '护甲 +{v}' },
  { key: 'atk', label: '攻击伤害 +{v}' },
  { key: 'mp', label: '最大灵力 +{v}' },
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[2, 5], [4, 9], [7, 14], [11, 20], [15, 26], [20, 34], [26, 42], [32, 52], [40, 64], [48, 78]]
  },
  {
    key: 'agility',
    label: '敏捷 +{v}',
    bands: [[2, 5], [4, 9], [7, 14], [11, 20], [15, 26], [20, 34], [26, 42], [32, 52], [40, 64], [48, 78]]
  },
  {
    key: 'intelligence',
    label: '智力 +{v}',
    bands: [[2, 5], [4, 9], [7, 14], [11, 20], [15, 26], [20, 34], [26, 42], [32, 52], [40, 64], [48, 78]]
  },
  {
    key: 'atkSpeed',
    label: '攻击速度提高 {v}%',
    bands: [[2, 4], [3, 6], [5, 8], [6, 10], [8, 12], [10, 14], [12, 17], [14, 20], [17, 24], [20, 28]]
  },
  {
    key: 'critChance',
    label: '暴击率 +{v}%',
    bands: [[1, 2], [2, 4], [3, 5], [4, 6], [5, 8], [6, 9], [7, 11], [8, 12], [10, 14], [11, 16]]
  },
  {
    key: 'critDamage',
    label: '暴击伤害 +{v}%',
    bands: [[4, 8], [7, 14], [10, 20], [14, 26], [18, 33], [22, 40], [26, 48], [30, 56], [35, 65], [40, 75]]
  }
];

/* 双属性词缀（一次加两项三维）：头盔 / 衣服 / 鞋子 / 手套 共用同一套数值区间 */
export const DUAL_ATTR_AFFIXES = [
  {
    key: 'strInt',
    label: '力量与智慧 +{v}',
    bands: [[5, 8], [7, 11], [9, 15], [12, 19], [15, 24], [19, 30], [24, 37], [29, 45], [35, 54], [42, 64]]
  },
  {
    key: 'strDex',
    label: '力量与敏捷 +{v}',
    bands: [[5, 8], [7, 11], [9, 15], [12, 19], [15, 24], [19, 30], [24, 37], [29, 45], [35, 54], [42, 64]]
  },
  {
    key: 'intDex',
    label: '智慧与敏捷 +{v}',
    bands: [[5, 8], [7, 11], [9, 15], [12, 19], [15, 24], [19, 30], [24, 37], [29, 45], [35, 54], [42, 64]]
  }
];

/* ============================================================
 * 头盔 / 衣服 共用词缀库（参考 PoE2 防具词缀）
 *   - hybridArmor：复合词缀「护甲提高 X%，生命上限 +Y」，占 1 条词缀但给两项数值
 * ============================================================ */
/* 防具基底防御（只给头盔 / 衣服 / 手套 / 鞋子）：
 *   每件防具按"三维词缀决定性质"随机 roll 一条基底 ——
 *     仅力量 → 护甲 ／ 仅敏捷 → 闪避 ／ 仅智慧 → 灵力
 *     （混搭按 力量 > 敏捷 > 智慧 取先；一件三维都没有的防具按护甲处理）
 *   数值按装备等级取档，表里的 minLevel 是这一档的最低装备等级：
 *     1 级 11~18 ／ 10 级 47~66 ／ 20 级 88~116 ／ 30 级 117~146
 *     40 级 147~176 ／ 50 级 177~207 ／ 60 级 235~261 ／ 70 级以上 262~300
 *   「防御力提高 X%」/「闪避提高 X%」最后再乘一遍总护甲 / 总闪避。 */
export const BASE_DEFENSE_BANDS = [
  { minLevel: 1, min: 11, max: 18 },
  { minLevel: 10, min: 47, max: 66 },
  { minLevel: 20, min: 88, max: 116 },
  { minLevel: 30, min: 117, max: 146 },
  { minLevel: 40, min: 147, max: 176 },
  { minLevel: 50, min: 177, max: 207 },
  { minLevel: 60, min: 235, max: 261 },
  { minLevel: 70, min: 262, max: 300 }
];

export const ARMOUR_AFFIXES = [
  {
    key: 'hp',
    label: '最大生命 +{v}',
    bands: [[5, 12], [12, 22], [22, 35], [35, 50], [50, 70], [70, 95], [95, 125], [125, 160], [160, 200], [200, 260]]
  },
  {
    key: 'armor',
    label: '护甲 +{v}',
    bands: [[6, 14], [14, 26], [26, 42], [42, 62], [62, 88], [88, 120], [120, 160], [160, 210], [210, 270], [270, 340]]
  },
  {
    key: 'hybridArmor',
    label: '护甲提高 {v}%，生命上限 +{v2}',
    bands: [[5, 10], [8, 16], [12, 22], [16, 28], [20, 35], [24, 42], [28, 50], [32, 58], [38, 68], [44, 80]],
    bands2: [[4, 10], [10, 18], [18, 28], [28, 40], [40, 56], [56, 76], [76, 100], [100, 128], [128, 160], [160, 210]]
  },
  {
    key: 'thorns',
    label: '荆棘伤害 +{v}',
    bands: [[2, 5], [5, 9], [9, 14], [14, 20], [20, 28], [28, 38], [38, 50], [50, 65], [65, 82], [82, 105]]
  },
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[3, 7], [6, 12], [10, 18], [15, 25], [20, 32], [26, 40], [32, 48], [40, 58], [48, 70], [58, 85]]
  },
  {
    key: 'chaosRes',
    label: '混沌抗性 +{v}%',
    bands: [[1, 3], [2, 5], [3, 7], [4, 9], [5, 11], [6, 13], [7, 15], [8, 17], [10, 20], [11, 23]]
  },
  {
    key: 'res',
    label: '全元素抗性 +{v}%',
    bands: [[2, 5], [4, 8], [6, 11], [8, 14], [10, 17], [12, 20], [14, 24], [16, 28], [18, 32], [20, 36]]
  },
  {
    key: 'lifeRegen',
    label: '生命每回合再生 +{v}',
    bands: [[1, 2], [2, 4], [3, 6], [4, 8], [6, 11], [8, 14], [10, 18], [12, 22], [15, 27], [18, 33]]
  },
  {
    // 闪避提高 X%：最后乘在"总闪避"上（和「防御力提高 X%」对护甲的关系一样）
    key: 'evasionPct',
    label: '闪避提高 {v}%',
    bands: [[4, 7], [5, 9], [6, 11], [8, 14], [9, 16], [11, 19], [13, 22], [15, 25], [17, 29], [19, 33]]
  },
  // 头盔 / 衣服也吃双属性词缀（力量+智慧 / 力量+敏捷 / 智慧+敏捷）
  ...DUAL_ATTR_AFFIXES
];

// 手套：在防具词缀库基础上，额外增加攻击类词缀
// 鞋子：走"分档位"的词缀库（和盾牌/防具一样，等级只决定最高档位），
// 不含攻击伤害，避免高等级鞋子滚出几十上百点的攻击/荆棘数值。
/* 衣服专属追加（头盔 / 手套 / 鞋子都不出这两条）：
 *   物理伤害减免 3%~7%、最大元素抗性上限 +1%~+5% */
export const CLOTHES_AFFIXES = ARMOUR_AFFIXES.concat([
  {
    key: 'physReducePct',
    label: '物理伤害减免 {v}%',
    bands: [[3, 4], [3, 4], [4, 5], [4, 5], [5, 5], [5, 6], [5, 6], [6, 7], [6, 7], [7, 7]]
  },
  {
    key: 'resCapPct',
    label: '最大元素抗性上限 +{v}%',
    bands: [[1, 1], [1, 2], [1, 2], [2, 2], [2, 3], [2, 3], [3, 4], [3, 4], [4, 5], [4, 5]]
  }
]);

export const SHOES_AFFIXES = [
  {
    key: 'speedPct',
    label: '移动速度提高 {v}%',
    bands: [[3, 5], [4, 6], [5, 8], [6, 9], [7, 11], [8, 13], [9, 15], [10, 15], [12, 15], [13, 15]]
  },
  // 三维属性：鞋子也能出力量 / 敏捷 / 智力（数值随装备等级档位提升）
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[1, 3], [2, 5], [3, 8], [5, 11], [7, 15], [9, 19], [12, 24], [15, 30], [18, 37], [22, 45]]
  },
  {
    key: 'agility',
    label: '敏捷 +{v}',
    bands: [[1, 3], [2, 5], [3, 8], [5, 11], [7, 15], [9, 19], [12, 24], [15, 30], [18, 37], [22, 45]]
  },
  {
    key: 'intelligence',
    label: '智力 +{v}',
    bands: [[1, 3], [2, 5], [3, 8], [5, 11], [7, 15], [9, 19], [12, 24], [15, 30], [18, 37], [22, 45]]
  },
  {
    key: 'hp',
    label: '最大生命 +{v}',
    bands: [[5, 12], [12, 22], [22, 35], [35, 50], [50, 70], [70, 95], [95, 125], [125, 160], [160, 200], [200, 260]]
  },
  {
    key: 'armor',
    label: '护甲 +{v}',
    bands: [[6, 14], [14, 26], [26, 42], [42, 62], [62, 88], [88, 120], [120, 160], [160, 210], [210, 270], [270, 340]]
  },
  {
    key: 'evasionPct',
    label: '闪避提高 {v}%',
    bands: [[4, 7], [5, 9], [6, 11], [8, 14], [9, 16], [11, 19], [13, 22], [15, 25], [17, 29], [19, 33]]
  },
  {
    key: 'mp',
    label: '最大灵力 +{v}',
    bands: [[4, 8], [8, 14], [14, 22], [22, 32], [32, 45], [45, 60], [60, 78], [78, 98], [98, 120], [120, 150]]
  },
  {
    key: 'res',
    label: '全元素抗性 +{v}%',
    bands: [[1, 3], [2, 5], [4, 7], [5, 9], [6, 11], [8, 13], [9, 16], [11, 18], [12, 21], [14, 24]]
  },
  {
    key: 'thorns',
    label: '荆棘伤害 +{v}',
    bands: [[2, 4], [4, 6], [5, 8], [7, 11], [9, 14], [12, 18], [15, 22], [19, 27], [23, 33], [28, 40]]
  },
  // 鞋子同样可以滚出双属性（力量+智慧 / 力量+敏捷 / 智慧+敏捷）
  ...DUAL_ATTR_AFFIXES
];

export const GLOVES_AFFIXES = ARMOUR_AFFIXES.concat([
  {
    key: 'atk',
    label: '攻击伤害 +{v}',
    bands: [[2, 5], [4, 8], [6, 12], [9, 16], [12, 20], [16, 26], [20, 32], [25, 40], [30, 48], [36, 58]]
  },
  {
    key: 'atkSpeed',
    label: '攻击速度提高 {v}%',
    bands: [[2, 4], [3, 6], [5, 8], [6, 10], [8, 12], [10, 14], [12, 17], [14, 20], [17, 24], [20, 28]]
  },
  {
    key: 'critChance',
    label: '暴击率 +{v}%',
    bands: [[1, 2], [2, 4], [3, 5], [4, 6], [5, 8], [6, 9], [7, 11], [8, 12], [10, 14], [11, 16]]
  },
  {
    key: 'critDamage',
    label: '暴击伤害 +{v}%',
    bands: [[4, 8], [7, 14], [10, 20], [14, 26], [18, 33], [22, 40], [26, 48], [30, 56], [35, 65], [40, 75]]
  },
  /* ---------- 手套专属：击杀 / 命中回复 + 双属性 + 异常状态几率 ----------
   * 数值同样按「10 级一档」成长：低级手套只落在第 1 档，越高级越高。 */
  {
    key: 'lifePerKill',
    label: '每击败一名敌人获得 {v} 生命',
    bands: [[2, 3], [3, 4], [4, 6], [5, 8], [7, 10], [9, 13], [11, 16], [14, 20], [17, 25], [21, 30]]
  },
  {
    key: 'mpPerKill',
    label: '每击败一名敌人获得 {v} 灵力',
    bands: [[2, 3], [3, 4], [4, 6], [5, 8], [7, 10], [9, 13], [11, 16], [14, 20], [17, 25], [21, 30]]
  },
  {
    key: 'lifePerHit',
    label: '攻击每击中一名敌人获得 {v} 生命',
    bands: [[1, 2], [2, 3], [3, 4], [4, 5], [5, 7], [6, 9], [8, 11], [10, 14], [12, 17], [15, 21]]
  },
  {
    key: 'poisonChance',
    label: '攻击时有 {v}% 几率造成中毒',
    bands: [[15, 20], [16, 22], [18, 24], [20, 26], [22, 28], [24, 30], [26, 33], [28, 35], [30, 38], [32, 40]]
  },
  {
    key: 'bleedChance',
    label: '攻击时有 {v}% 几率造成流血',
    bands: [[15, 20], [16, 22], [18, 24], [20, 26], [22, 28], [24, 30], [26, 33], [28, 35], [30, 38], [32, 40]]
  },
  {
    key: 'igniteChance',
    label: '攻击时有 {v}% 几率造成点燃',
    bands: [[15, 20], [16, 22], [18, 24], [20, 26], [22, 28], [24, 30], [26, 33], [28, 35], [30, 38], [32, 40]]
  }
]);

/* ============================================================
 * 首饰词缀库（项链 / 戒指一类的饰品）
 *   参考 PoE：生命 / 三抗 / 混沌抗 / 属性 / 护甲 / 生命再生 / 灵力
 * ============================================================ */
export const JEWELRY_AFFIXES = [
  {
    key: 'hp',
    label: '最大生命 +{v}',
    bands: [[5, 12], [12, 22], [22, 35], [35, 50], [50, 70], [70, 95], [95, 125], [125, 160], [160, 200], [200, 260]]
  },
  {
    key: 'res',
    label: '全元素抗性 +{v}%',
    bands: [[2, 5], [4, 8], [6, 11], [8, 14], [10, 17], [12, 20], [14, 24], [16, 28], [18, 32], [20, 36]]
  },
  {
    key: 'chaosRes',
    label: '混沌抗性 +{v}%',
    bands: [[1, 3], [2, 5], [3, 7], [4, 9], [5, 11], [6, 13], [7, 15], [8, 17], [10, 20], [11, 23]]
  },
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[3, 7], [6, 12], [10, 18], [15, 25], [20, 32], [26, 40], [32, 48], [40, 58], [48, 70], [58, 85]]
  },
  {
    key: 'armor',
    label: '护甲 +{v}',
    bands: [[6, 14], [14, 26], [26, 42], [42, 62], [62, 88], [88, 120], [120, 160], [160, 210], [210, 270], [270, 340]]
  },
  {
    key: 'lifeRegen',
    label: '生命每回合再生 +{v}',
    bands: [[1, 2], [2, 4], [3, 6], [4, 8], [6, 11], [8, 14], [10, 18], [12, 22], [15, 27], [18, 33]]
  },
  {
    key: 'mp',
    label: '最大灵力 +{v}',
    bands: [[3, 8], [6, 14], [10, 22], [15, 32], [20, 44], [26, 58], [32, 72], [40, 90], [48, 110], [58, 132]]
  }
];

// ============================================================
// 世界BOSS
// · 目前只开放一个：「森特欧斯萨满」（等级 30 · 赤沙祭坛）
// · refreshMin = 刷新周期（分钟）：刷新后可挑战 3 分钟，没被击杀就自己消失，
//   剩下的时间进入冷却，到点再次刷新。
// · 后期联网后，这里会改成"全服共享血量 + 伤害排行榜结算"。
// ============================================================
/* ============================================================
 * 世界BOSS · 名单
 *   全部已开放：每次刷新（上海时间 每 2 小时 15 分）后的 3 分钟窗口内，
 *   只要境界达标就能任选一个传送过去挑战。
 *   theme 决定战场场景（见 main.js 的 WB_THEMES）：天空 / 地面 / 粒子都跟着换。
 *   art   地图立绘（透明 WebP，统一 704×644 画框）
 *   info  鼠标停在列表行上弹出的资料图
 * ============================================================ */
export const WORLD_BOSSES = [
  {
    id: 'stos', level: 10, name: '森特欧斯萨满', place: '赤沙祭坛',
    open: true, sprite: 'yaoshi', hp: 7600, scale: 2.6, theme: 'bloodsand',
    art: 'assets/img/boss/stos.png',
    info: 'assets/img/boss/stos_info.png',
    infoDetail: '赤沙祭坛的萨满祭司 · 用血沙献祭整片沙漠的游魂'
  },
  {
    id: 'wastelord', level: 14, name: '荒原统领', place: '枯骨荒原',
    open: true, sprite: 'youmin', hp: 9000, scale: 2.5, theme: 'wasteland',
    art: 'assets/img/boss/wastelord.webp',
    infoDetail: '枯骨荒原的执鞭者 · 把败者的旗杆插成一整条地平线'
  },
  {
    id: 'rebelgeneral', level: 20, name: '叛旗督军', place: '残旗古战场',
    open: true, sprite: 'bingfei', hp: 11500, scale: 2.5, theme: 'banner',
    art: 'assets/img/boss/rebelgeneral.webp',
    infoDetail: '残旗古战场的督军 · 身后跟着一支永远不会解散的败军'
  },
  {
    id: 'miasmaworm', level: 26, name: '瘴纹蠕主', place: '毒瘴湿地',
    open: true, sprite: 'qigai', hp: 14500, scale: 2.7, theme: 'miasma',
    art: 'assets/img/boss/miasmaworm.webp',
    info: 'assets/img/boss/miasmaworm_info.jpg',
    infoDetail: '毒瘴湿地的蠕主 · 呼出的每一口气都是会走路的瘟疫'
  },
  {
    id: 'rockqueen', level: 32, name: '岩壳女王', place: '磐石巢穴',
    open: true, sprite: 'shanzhu', hp: 18000, scale: 2.6, theme: 'rockshell',
    art: 'assets/img/boss/rockqueen.webp',
    infoDetail: '磐石巢穴的女王 · 甲壳上还嵌着上一个挑战者的兵器'
  },
  {
    id: 'earthmother', level: 38, name: '壤脉掘母', place: '壤脉地窟',
    open: true, sprite: 'yaoshi', hp: 21500, scale: 2.8, theme: 'earth',
    art: 'assets/img/boss/earthmother.webp',
    info: 'assets/img/boss/earthmother_info.jpg',
    infoDetail: '壤脉地窟的掘母 · 在地底啃出一条又一条通往城镇的隧道'
  },
  {
    id: 'rockcolossus', level: 44, name: '碎脉岩崩巨像', place: '崩岩峡谷',
    open: true, sprite: 'shanzhu', hp: 25500, scale: 3.0, theme: 'rockfall',
    art: 'assets/img/boss/rockcolossus.webp',
    info: 'assets/img/boss/rockcolossus_info.jpg',
    infoDetail: '崩岩峡谷的巨像 · 每迈一步就带下一整面山壁'
  },
  {
    id: 'frostfang', level: 50, name: '寒霜牙裔', place: '冻牙雪原',
    open: true, sprite: 'shuifei', hp: 30000, scale: 2.6, theme: 'frost',
    art: 'assets/img/boss/frostfang.webp',
    info: 'assets/img/boss/frostfang_info.jpg',
    infoDetail: '冻牙雪原的霜裔 · 吐息能把整片雪原冻成一面镜子'
  },
  {
    id: 'shadowbeast', level: 56, name: '暗影巨兽', place: '蚀月暗窟',
    open: true, sprite: 'qiangdao', hp: 34500, scale: 2.9, theme: 'shadow',
    art: 'assets/img/boss/shadowbeast.webp',
    info: 'assets/img/boss/shadowbeast_info.jpg',
    infoDetail: '蚀月暗窟的巨兽 · 它不吃人，它吃光'
  },
  {
    id: 'vortexlord', level: 60, name: '沧涡噬主', place: '万涛沉船湾',
    open: true, sprite: 'shuifei', hp: 38000, scale: 2.9, theme: 'vortex',
    art: 'assets/img/boss/vortexlord.webp',
    infoDetail: '万涛沉船湾的噬主 · 漩涡中心沉着十几条没见过天日的船'
  },
  {
    id: 'lavaking', level: 63, name: '熔岩火灵', place: '焚心熔窟',
    open: true, sprite: 'nongfu', hp: 41000, scale: 2.7, theme: 'lava',
    art: 'assets/img/boss/lavaking.webp',
    info: 'assets/img/boss/lavaking_info.jpg',
    infoDetail: '焚心熔窟的火灵 · 岩壳裂开一道缝，里面就是它的眼睛'
  },
  {
    id: 'moltenwall', level: 65, name: '赤熔摧垣', place: '熔渣壁垒',
    open: true, sprite: 'kuaiji', hp: 43500, scale: 3.0, theme: 'molten',
    art: 'assets/img/boss/moltenwall.webp',
    info: 'assets/img/boss/moltenwall_info.jpg',
    infoDetail: '熔渣壁垒的摧垣 · 它走过的地方，城墙只剩一摊红铁水'
  },
  {
    id: 'forgeguard', level: 67, name: '锻枢卫士', place: '古锻工坊',
    open: true, sprite: 'yaoshi', hp: 46000, scale: 2.8, theme: 'forge',
    art: 'assets/img/boss/forgeguard.webp',
    info: 'assets/img/boss/forgeguard_info.jpg',
    infoDetail: '古锻工坊的卫士 · 至今还在替一座早已熄灭的炉子守门'
  }
];

/* ============================================================
 * 灵根（武者大圆满 → 炼气 突破时随机获得一条）
 *   · 突破成功那一刻自动随机一条并弹窗；不满意可以花 SPIRIT_ROOT_REROLL_COST 金币再随
 *   · 权重越小越稀有（天灵根 2%，伪灵根最烂也最常见）
 *   · mods 直接加在派生属性上，字段和图鉴增幅 / 装备词缀同一套（见 main.js 的 recalcPlayerStats）
 *   · 角色窗口「灵根资质」那行显示当前灵根，并提供重新随机按钮
 *   · 以后加灵根只要往下面这张表里补一条（weight 决定稀有度）
 * ============================================================ */
/* 重新随机灵根的花费：1 枚天地灵物 + 10000 金币（第一次突破时的灵根是免费的） */
export const SPIRIT_ROOT_REROLL_COST = 10000;
export const SPIRIT_ROOT_REROLL_RELIC = 1;
export const SPIRIT_ROOTS = [
  /* 权重按 1000 计：伪灵根 850 = 85%，其余 15% 按稀有度分给另外 8 条 */
  { id: 'tian', name: '天灵根', grade: '极品', weight: 4, color: '#ffd76a',
    desc: '五气朝元：法术伤害 +15%、全能力 +5',
    effects: ['法术伤害 +15%', '力量 / 敏捷 / 智慧 各 +5'],
    mods: { spellDmgPct: 15, strength: 5, agility: 5, intelligence: 5 } },
  { id: 'lei', name: '雷灵根', grade: '上品', weight: 15, color: '#c9b0ff',
    desc: '雷性锋锐：攻击伤害 +12、暴击率 +5%',
    effects: ['攻击伤害 +12', '暴击率 +5%'], mods: { atk: 12, critChance: 5 } },
  { id: 'bing', name: '冰灵根', grade: '上品', weight: 15, color: '#9fe4ff',
    desc: '寒性护体：防御力提高 15%、混沌抗性 +5%',
    effects: ['防御力提高 15%', '混沌抗性 +5%'], mods: { armorPct: 15, chaosRes: 5 } },
  { id: 'feng', name: '风灵根', grade: '上品', weight: 15, color: '#b8ffd8',
    desc: '风性轻灵：移动速度 +12%、暴击率 +4%',
    effects: ['移动速度 +12%', '暴击率 +4%'], mods: { speedPct: 12, critChance: 4 } },
  { id: 'yuanci', name: '元磁灵根', grade: '上品', weight: 15, color: '#a9d0ff',
    desc: '磁御万法：全元素抗性 +10%、格挡几率 +5%',
    effects: ['全元素抗性 +10%', '格挡几率 +5%'], mods: { res: 10, blockPct: 5 } },
  { id: 'yin', name: '隐灵根', grade: '中品', weight: 26, color: '#b9c6d8',
    desc: '匿形藏锋：暴击伤害 +20%、攻击伤害 +6',
    effects: ['暴击伤害 +20%', '攻击伤害 +6'], mods: { critDamage: 20, atk: 6 } },
  { id: 'dan', name: '丹灵根', grade: '中品', weight: 26, color: '#ffb9c8',
    desc: '丹性养生：生命上限 +80、生命每回合再生 +5',
    effects: ['生命上限 +80', '生命每回合再生 +5'], mods: { hp: 80, lifeRegen: 5 } },
  { id: 'zhen', name: '真灵根', grade: '良品', weight: 34, color: '#ffd0a0',
    desc: '中正平和：生命上限 +60、攻击伤害 +5、防御力提高 8%',
    effects: ['生命上限 +60', '攻击伤害 +5', '防御力提高 8%'],
    mods: { hp: 60, atk: 5, armorPct: 8 } },
  { id: 'wei', name: '伪灵根', grade: '下品', weight: 850, color: '#9aa4b0',
    desc: '杂而不纯：生命上限 +20',
    effects: ['生命上限 +20'], mods: { hp: 20 } }
];
